// @flow

import { type UserInputValidator, type UserInputValidationError, type UserInputValidatorArgs } from './types';

import { chileanValidator } from '@tectonics/prs/chile/validation';
import { mexicanValidator } from '@tectonics/prs/mexico/validation';
import { peruvianValidator } from '@tectonics/prs/peru/validation';
import { brazilianValidator } from '@tectonics/prs/brazil/validation';
import { colombianValidator } from '@tectonics/prs/colombia/validation';
import { argentineValidator } from '@tectonics/prs/argentina/validation';
import { uruguayanValidator } from '@tectonics/prs/uruguay/validation';
import { ecuadorianValidator } from '@tectonics/prs/ecuador/validation';
import { bolivianValidator } from '@tectonics/prs/bolivia/validation';

import { captchaValidator } from '@tectonics/captcha';
import { voidValidator } from '@tectonics/prs/validation';
import { customerValidator } from '@tectonics/prs/customer/validation';

import { tefValidator } from '@tectonics/prs/tef/validation';
import { speiValidator } from '@tectonics/prs/spei/validation';
import { cardValidator } from '@tectonics/prs/card/validation';
import { webpayValidator } from '@tectonics/prs/webpay/validation';
import { boletoValidator } from '@tectonics/prs/boleto/validation';
import { balotoValidator } from '@tectonics/prs/baloto/validation';
import { sencillitoValidator } from '@tectonics/prs/sencillito/validation';
import { pagoefectivoValidator } from '@tectonics/prs/pagoefectivo/validation';
import { safetypayValidator } from '@tectonics/prs/safetypay/validation';
import { multicajaValidator } from '@tectonics/prs/multicaja/validation';
import { servipagValidator } from '@tectonics/prs/servipag/validation';
import { eftValidator } from '@tectonics/prs/eft/validation';
import { cashMexicoValidator } from '@tectonics/prs/cash-mexico/validation';
import { cashBoliviaValidator } from '@tectonics/prs/cash-bolivia/validation';
import { safetypayCashValidator } from '@tectonics/prs/safetypay-cash/validation';
import { safetypayOnlineValidator } from '@tectonics/prs/safetypay-online/validation';
import { argentineCashValidator } from '@tectonics/prs/cash-argentina/validation';
import { ewalletValidator } from '@tectonics/prs/ewallet/validation';
import { efectyValidator } from '@tectonics/prs/efecty/validation';

import type { Country } from '@tectonics/prs/country';

export function getUserInputValidator(selectedPaymentMethodCode: string, country: Country): UserInputValidator {
  return compositeValidator([
    customerValidator,
    captchaValidator,
    getCountryValidator(country),
    getPaymentMethodValidator(selectedPaymentMethodCode, country),
  ]);
}

function getCountryValidator(country: Country): UserInputValidator{
  switch (country) {
    case 'br':
      return brazilianValidator;
    case 'ar':
      return argentineValidator;
    case 'bo':
      return bolivianValidator;
    case 'cl':
      return chileanValidator;
    case 'co':
      return colombianValidator;
    case 'ec':
      return ecuadorianValidator;
    case 'mx':
      return mexicanValidator;
    case 'pe':
      return peruvianValidator;
    case 'uy':
      return uruguayanValidator;
    default:
      throw new Error(`No customer form validator for country ${country}`);
  }
}

function compositeValidator(validators: ImmutableArray<UserInputValidator>): UserInputValidator {
  return (args: UserInputValidatorArgs): ?UserInputValidationError => {
    for (const validator of validators) {
      const result = validator(args);

      if (result) {
        return result;
      }
    }

    return undefined;
  };
}

function getPaymentMethodValidator(selectedPaymentMethod: string, country: Country): UserInputValidator {
  switch (selectedPaymentMethod) {
    case 'creditcard':
    case 'debitcard':
      return cardValidator;

    case 'boleto':
      return boletoValidator;

    case 'tef':
      return tefValidator;

    case 'banktransfer':
    case 'oxxo':
    case 'oxxopay':
    case 'google':
    case 'pix':
      return voidValidator;

    case 'spei':
      return speiValidator;

    case 'cash':
      return cashValidatorResolver(country);

    case 'baloto':
      return balotoValidator;

    case 'multicaja':
      return multicajaValidator;

    case 'servipag':
      return servipagValidator;

    case 'eft':
      return eftValidator;

    case 'sencillito':
      return sencillitoValidator;

    case 'webpay':
      return webpayValidator;

    case 'pagoefectivo':
      return pagoefectivoValidator;

    case 'safetypay':
      return safetypayValidator;

    case 'safetypay-cash':
      return safetypayCashValidator;

    case 'safetypay-online':
      return safetypayOnlineValidator;

    case 'ewallet':
      return ewalletValidator;

    case '':
      return voidValidator;

    default:
      throw new Error(`validator for method: ${selectedPaymentMethod} not found`);
  }
}

function cashValidatorResolver(country: Country): UserInputValidator {
  switch (country) {
    case 'mx':
      return cashMexicoValidator;
    case 'bo':
      return cashBoliviaValidator;
    case 'ar':
      return argentineCashValidator;
    case 'co':
      return efectyValidator;
    default:
      throw new Error(`validator for method cash in country ${country} not found`);
  }
}
